import { Client } from '@modelcontextprotocol/sdk/client';

// Initialize client once
const client = new Client();

interface TrendScore {
  engagement: number;
  virality: number;
  sentiment: number;
  total: number;
}

interface TrendAnalysis {
  element: string;
  platform: string;
  score: TrendScore;
}

interface TopTrends {
  themes?: Array<{ name: string; score: number }>;
  characters?: Array<{ name: string; score: number }>;
  plots?: Array<{ name: string; score: number }>;
}

class SocialTrendsService {
  private async analyzeTrend(element: string, platform: string = 'all'): Promise<TrendAnalysis> {
    const result = await client.callTool('social-trends', 'analyze_trend', {
      element,
      platform
    });

    return JSON.parse(result.content[0].text);
  }

  private async getTopTrends(category: string = 'all', limit: number = 5): Promise<TopTrends> {
    const result = await client.callTool('social-trends', 'get_top_trends', {
      category,
      limit
    });

    return JSON.parse(result.content[0].text);
  }

  async getOptimizedElements(): Promise<{
    theme: string;
    plotElements: string[];
    characterTypes: string[];
  }> {
    try {
      // Get top trending elements across all categories
      const trends = await this.getTopTrends('all', 5);

      // Select highest scoring elements
      const theme = trends.themes?.[0]?.name || 'identity_crisis';
      const plotElements = trends.plots?.slice(0, 2).map(p => p.name) || ['public_revelation', 'unexpected_alliance'];
      const characterTypes = trends.characters?.slice(0, 2).map(c => c.name) || ['rebel_artist', 'popular_facade'];

      // Analyze engagement for selected theme
      const themeAnalysis = await this.analyzeTrend(theme);

      // Log analysis for monitoring
      console.log('Theme Analysis:', themeAnalysis);

      return {
        theme,
        plotElements,
        characterTypes
      };
    } catch (error) {
      console.error('Failed to get optimized elements:', error);
      // Return default elements if analysis fails
      return {
        theme: 'identity_crisis',
        plotElements: ['public_revelation', 'unexpected_alliance'],
        characterTypes: ['rebel_artist', 'popular_facade']
      };
    }
  }

  async analyzeEngagement(elements: string[]): Promise<Map<string, TrendScore>> {
    const results = new Map<string, TrendScore>();

    try {
      // Analyze each element in parallel
      const analyses = await Promise.all(
        elements.map(element => this.analyzeTrend(element))
      );

      // Store results in map
      analyses.forEach(analysis => {
        results.set(analysis.element, analysis.score);
      });
    } catch (error) {
      console.error('Failed to analyze engagement:', error);
    }

    return results;
  }
}

export const socialTrendsService = new SocialTrendsService();
